import "./style.scss";

import Search from "@mui/icons-material/Search";
import { Modal } from "@mui/material";
import type { TTags } from "components/Title";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchBrands } from "store/brandStore";
import { useAppDispatch, useAppSelector } from "store/store";

import BrandAutocomplete from "../BrandAutocomplete";
import ChipsAutocomplete from "../ChipsAutocomplete";
import ControlFilter from "../ControlFilter";
import SmallSearchBar from "../SmallSearchBar";
import StyledTextField from "../StyledTextField/StyledTextField";
import LocationsManagement from "./LocationsManagement";

interface IStockFilter {
    onImport: () => void;
    onExport: () => void;
    tags: TTags[] | null;
    onSelection: React.Dispatch<React.SetStateAction<string[]>>;
    onControlChange: React.Dispatch<React.SetStateAction<"all" | "valid" | "invalid">>;
    onQuery: (e: React.ChangeEvent<HTMLInputElement>) => void;
    filter: string;
    control: "all" | "valid" | "invalid";
    setChosenBrand: React.Dispatch<React.SetStateAction<string>>;
}

const StockFilter = ({
    tags,
    onSelection,
    onControlChange,
    control,
    onQuery,
    filter,
    setChosenBrand,
}: IStockFilter) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();

    const [modalStatus, setModalStatus] = useState(false);
    const [chosenTags, setChosenTags] = useState<string[]>([]);

    const { getCompanySettings, companyInfos } = useCompanySettings();
    const hasBrands = getCompanySettings("uses_multiple_brands");
    const { brands } = useAppSelector((state) => state.brands);

    useEffect(() => {
        if (hasBrands && !brands.length) dispatch(fetchBrands(true));
    }, [hasBrands, brands]);

    const controlFilterOptions = [
        { value: "all", label: `${t("Stock.FilterAll")}` },
        { value: "valid", label: `${t("Stock.Valid")}` },
        { value: "invalid", label: `${t("Stock.Invalid")}` },
    ];

    return (
        <div className={`stock-page-filter ${hasBrands ? "brands" : ""}`}>
            {/* Modal that shows stock locations and redirects to Settings page */}
            <Modal open={modalStatus} onClose={() => setModalStatus(false)}>
                <LocationsManagement onClose={() => setModalStatus(false)} />
            </Modal>

            {/* Search bar */}
            <SmallSearchBar filter={filter} onSearch={onQuery} />

            {/* Tags and control filters */}
            <div className="stock-filters-select">
                {hasBrands && (
                    <BrandAutocomplete
                        brands={brands}
                        setChosenBrand={setChosenBrand}
                        companyName={companyInfos?.name}
                    />
                )}
                <ChipsAutocomplete
                    options={tags ?? []}
                    label={t("Stock.Titles")}
                    filter={filter}
                    noOptionsText={t("Filters.EmptyStateTitleTags")}
                    onSelection={onSelection}
                    chosenTags={chosenTags}
                    setChosenTags={setChosenTags}
                />
                <ControlFilter
                    filterLabel={t("Stock.Control")}
                    filterOptions={controlFilterOptions}
                    controlValue={control}
                    onValueChange={(value) => onControlChange(value)}
                />
            </div>
        </div>
    );
};

export default StockFilter;
